import React, { useState } from 'react';
import {
	Box,
	IconButton,
	MenuItem,
	Select,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { RiArrowLeftLine } from 'react-icons/ri';
import { Trans, useTranslation } from 'services/localization';
import BackdropDialog from 'common/components/BackdropDialog';
import { Callable } from 'common/frontend/callable';
import {
	DomainDetails,
	getCustomDomainARecordValue,
	getCustomDomainCnameValue,
} from 'common/modules/customDomains';
import { isDevEnv, notUndefined } from 'common/utils/common';
import { RecordValidationResult, validateARecords, validateCnameRecord } from '../utils';
import CopyToClipboardIcon from 'components/CopyToClipboardIcon';
import ExternalLink from 'components/ExternalLink';
import errorHandler from 'common/services/errorHandler';
import Banner from 'components/Banner';
interface Props {
	shopId: string;
	domainDetails: DomainDetails;
	onBackClick: () => void;
	onClose: () => void;
	isExistingDomain: boolean;
}

interface DnsRecordRow {
	record: 'CNAME' | 'A';
	host: string;
	value: string;
}

const VerifyDomain = (props: Props) => {
	const { t } = useTranslation();
	const devEnv = isDevEnv();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const { onBackClick, onClose, shopId, isExistingDomain, domainDetails } = props;
	const [domainSetupError, setDomainSetupError] = useState<string | null>(null);
	const [setupType, setSetupType] = useState<'basic' | 'advanced'>('basic');
	const [verificationErrors, setVerificationErrors] = useState<string[]>([]);
	const [domainSetupLoading, setDomainSetupLoading] = useState<'no-verify' | 'verify' | null>(null);

	const domainName = domainDetails.formattedDomainName;
	const cNameDomainName = domainName;
	const aRecordsDomainName = domainDetails.withoutWww;
	const isRootDomain = !domainDetails.isSubdomain;
	const requireARecord = isRootDomain && setupType === 'basic';
	const isAdvancedSetup = setupType === 'advanced';

	const cNameRecordRow: DnsRecordRow = {
		record: 'CNAME',
		host: domainDetails.subdomainPart ?? 'www',
		value: getCustomDomainCnameValue(devEnv),
	};

	const aRecordRow: DnsRecordRow = {
		record: 'A',
		host: '@',
		value: getCustomDomainARecordValue(devEnv),
	};

	const handleVerify = async () => {
		setVerificationErrors([]);
		setDomainSetupLoading('verify');
		let verificationErrors: string[] = [];
		try {
			const [cnameValidation, aRecordValidation]: [
				RecordValidationResult,
				RecordValidationResult,
			] = await Promise.all([
				validateCnameRecord(cNameDomainName, t),
				requireARecord ? validateARecords(aRecordsDomainName, t) : { valid: true },
			]);
			if (!cnameValidation.valid) {
				verificationErrors.push(cnameValidation.error);
			}
			if (!aRecordValidation.valid) {
				verificationErrors.push(aRecordValidation.error);
			}
		} catch (e) {
			errorHandler.report(e);
			verificationErrors.push(t('common:errors.somethingWentWrong'));
		}
		if (!!verificationErrors.length) {
			setVerificationErrors(verificationErrors);
			setDomainSetupLoading(null);
			return;
		}
		try {
			await Callable.customDomains.createCustomDomain({
				merchantId: shopId,
				domainName,
				dnsVerified: true,
				isExistingDomain,
			});
		} catch (e) {
			setDomainSetupError(t('common:errors.somethingWentWrong'));
			setDomainSetupLoading(null);
			return;
		}
		setDomainSetupLoading(null);
		onClose();
	};

	const handleVerifyLater = async () => {
		setVerificationErrors([]);
		setDomainSetupError(null);
		setDomainSetupLoading('no-verify');
		try {
			await Callable.customDomains.createCustomDomain({
				merchantId: shopId,
				domainName,
				dnsVerified: false,
				isExistingDomain,
			});
		} catch (e) {
			setDomainSetupError(t('common:errors.somethingWentWrong'));
			setDomainSetupLoading(null);
			return;
		}
		setDomainSetupLoading(null);
		onClose();
	};

	const dnsRecordRows = [cNameRecordRow, requireARecord ? aRecordRow : undefined].filter(
		notUndefined,
	);

	return (
		<BackdropDialog
			fullScreen={isMobile}
			isOpen={true}
			onClose={onClose}
			label={
				<Box display="flex" alignItems="center" color="white">
					{!isExistingDomain && (
						<IconButton onClick={onBackClick} color="inherit" aria-label={t('common:actions.back')}>
							<RiArrowLeftLine color="white" />
						</IconButton>
					)}
					<Typography variant="h6">{t('account.domains.verifyDomain', 'Verify domain')}</Typography>
				</Box>
			}
			body={
				<>
					{!!verificationErrors.length && (
						<Banner
							variant="danger"
							sx={{ mb: 2 }}
							title={t('account.domains.verificationFailed', 'Verification failed')}
							description={
								<Box>
									{verificationErrors.map((error, i) => (
										<Typography key={`error_${i}`} variant="body2" sx={{ fontWeight: 400 }}>
											{'&bull;'}
											{error}
										</Typography>
									))}
									<Typography variant="body2" sx={{ mt: 2, fontWeight: 400 }}>
										{t(
											'account.domains.dnsUpdateNote',
											'Note: DNS records might take up to 24 hours to update.',
										)}
									</Typography>
								</Box>
							}
						/>
					)}
					{!!domainSetupError && (
						<Banner variant="danger" title={domainSetupError} sx={{ mb: 2 }} />
					)}
					{isRootDomain && (
						<Select
							sx={{
								mb: 2,
								width: {
									xs: '100%',
									sm: 300,
								},
								height: 40,
							}}
							MenuProps={{
								sx: {
									zIndex: 1302,
									maxWidth: {
										xs: '100%',
										sm: 300,
									},
								},
							}}
							value={setupType}
							renderValue={(option) =>
								option === 'advanced'
									? t('account.domains.advancedSetup', 'Advanced setup')
									: t('account.domains.basicSetup', 'Basic setup')
							}
							onChange={(e) => {
								setVerificationErrors([]);
								setSetupType(e.target.value as 'basic' | 'advanced');
							}}
						>
							<MenuItem value="basic">{t('account.domains.basicSetup', 'Basic setup')}</MenuItem>
							<MenuItem value="advanced" sx={{ display: 'block', whiteSpace: 'normal' }}>
								<Typography>{t('account.domains.advancedSetup', 'Advanced setup')}</Typography>
								<Typography
									variant="caption"
									color="secondary"
									sx={{ display: 'inline-block', lineHeight: 1.3 }}
								>
									{t(
										'account.domains.advancedSetupInfo',
										'Add HTTPS-redirection from root to www domain',
									)}
								</Typography>
							</MenuItem>
						</Select>
					)}
					<Typography variant="body2">
						{isAdvancedSetup ? '1.' : ''}{' '}
						<Trans
							i18nKey="account.domains.addDnsRecordsDescription"
							defaults="Add these DNS records to your domain <b>{{domain}}</b> by visiting your domain provider"
							components={{
								b: <b />,
							}}
							values={{
								domain: domainDetails.rootDomain,
							}}
						/>
						.
					</Typography>
					<Box sx={{ overflow: 'auto' }}>
						<Table
							sx={{ mt: 2, tableLayout: isMobile ? 'auto' : 'fixed', minWidth: 380 }}
							size="small"
						>
							<DnsRecordTableHead />
							<TableBody>
								{dnsRecordRows.map(({ record, host, value }, i) => (
									<TableRow key={value + i}>
										<TableCell>{record}</TableCell>
										<TableCell>{host}</TableCell>
										<TableCell>
											<Box display="flex" alignItems="center" justifyContent="space-between">
												<span>{value}</span>
												<CopyToClipboardIcon
													text={value}
													showTooltip={true}
													showCopyNotification={true}
												/>
											</Box>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</Box>
					{isAdvancedSetup ? (
						<Typography variant="body2" sx={{ mt: 4 }}>
							{'2. '}
							<Trans
								i18nKey="account.domains.setupHttpsRedirection"
								defaults="Configure your own HTTPS redirection from your root domain to the www domain. This needs to be
							configured outside of Rentle. Read more on <supportLink>how to set up root domain HTTPS redirection</supportLink>"
								components={{
									supportLink: (
										<ExternalLink
											href={
												'https://support.rentle.io/en/root-domain-is-not-loading-over-https#configure-root-domain-https-redirection'
											}
										>
											{'how to set up root domain HTTPS redirection'}
										</ExternalLink>
									),
								}}
							/>
							.
						</Typography>
					) : (
						<Typography variant="body2" color="secondary" sx={{ mt: 4 }}>
							<Trans
								i18nKey="account.domains.verificationInstructions"
								defaults="See <supportLink>detailed instructions</supportLink> on how to verify a domain"
								components={{
									supportLink: (
										<ExternalLink href={'https://support.rentle.io/en/custom-domain'}>
											{'detailed instructions'}
										</ExternalLink>
									),
								}}
							/>
						</Typography>
					)}
				</>
			}
			leftActionBtn={
				isExistingDomain || isMobile
					? undefined
					: {
							label: t('common:actions.cancel'),
							disableAutoClose: true,
							callback: onClose,
					  }
			}
			cancel={{
				label: isExistingDomain
					? t('common:actions.cancel')
					: t('account.domains.verifyLater', 'Verify later'),
				loading: domainSetupLoading === 'no-verify',
				disableAutoClose: true,
				callback: isExistingDomain ? onClose : handleVerifyLater,
				disabled: !!domainSetupLoading,
			}}
			confirm={{
				label: t('common:actions.verify', 'Verify'),
				loading: domainSetupLoading === 'verify',
				disableAutoClose: true,
				callback: handleVerify,
				disabled: !!domainSetupLoading,
			}}
			backdropTimeout={0}
			disableCloseOnClickAway
		/>
	);
};

const DnsRecordTableHead = () => {
	const { t } = useTranslation();
	return (
		<TableHead>
			<TableRow>
				<TableCell>{t('account.domains.recordType', 'Record type')}</TableCell>
				<TableCell>{t('common:form.name')}</TableCell>
				<TableCell>{t('common:form.value', 'Value')}</TableCell>
			</TableRow>
		</TableHead>
	);
};

export default VerifyDomain;
